import api, { uploadApi } from './axios';

/**
 * Get all posts with pagination and filters
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.visibility - Post visibility filter
 * @returns {Promise} Promise object representing the API response
 */
export const getPosts = (options = {}) => {
  const { page = 1, limit = 10, visibility = 'public' } = options;
  
  return api.get('/posts', {
    params: {
      page,
      limit,
      visibility,
    },
  });
};

/**
 * Get user feed with pagination and feed type
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.feedType - Type of feed (following, discover, community)
 * @returns {Promise} Promise object representing the API response
 */
export const getUserFeed = (options = {}) => {
  const { page = 1, limit = 10, feedType = 'following' } = options;
  
  return api.get('/posts/feed', {
    params: {
      page,
      limit,
      feedType,
    },
  });
};

/**
 * Create a new post with optional media
 * @param {Object|FormData} postData - Post data or FormData with post data and media
 * @param {Function} onUploadProgress - Progress callback
 * @returns {Promise} Promise object representing the API response
 */
export const createPost = (postData, onUploadProgress) => {
  // Check if postData is already FormData
  if (postData instanceof FormData) {
    return uploadApi('/posts', postData, onUploadProgress);
  }
  
  // If not FormData, create a new FormData and append the data
  const { content, visibility = 'public', communityId, media = [] } = postData;
  
  const formData = new FormData();
  formData.append('content', content);
  formData.append('visibility', visibility);
  
  if (communityId) {
    formData.append('communityId', communityId);
  }
  
  // Append multiple media files
  media.forEach((file) => {
    formData.append('media', file);
  });
  
  return uploadApi('/posts', formData, onUploadProgress);
};

/**
 * Get a post by ID
 * @param {string} postId - Post ID
 * @returns {Promise} Promise object representing the API response
 */
export const getPostById = async (postId) => {
  try {
    if (!postId) {
      throw new Error('Post ID is required');
    }
    
    console.log(`Requesting post with ID: ${postId}`);
    const response = await api.get(`/posts/${postId}`);
    console.log('Response received:', response);

    return response;
  } catch (error) {
    console.error(`Error in getPostById for ${postId}:`, error);
    throw error;
  }
};

/**
 * Update a post
 * @param {string} postId - Post ID
 * @param {Object} postData - Updated post data
 * @returns {Promise} Promise object representing the API response
 */
export const updatePost = (postId, postData) => {
  return api.put(`/posts/${postId}`, postData);
};

/**
 * Delete a post
 * @param {string} postId - Post ID
 * @returns {Promise} Promise object representing the API response
 */
export const deletePost = (postId) => {
  return api.delete(`/posts/${postId}`);
};

/**
 * Like a post
 * @param {string} postId - Post ID
 * @returns {Promise} Promise object representing the API response
 */
export const likePost = (postId) => {
  return api.put(`/posts/${postId}/like`);
};

/**
 * Unlike a post
 * @param {string} postId - Post ID
 * @returns {Promise} Promise object representing the API response
 */
export const unlikePost = (postId) => {
  return api.delete(`/posts/${postId}/like`);
};

/**
 * Get posts by a specific user
 * @param {string} userId - User ID
 * @param {Object} options - Pagination options
 * @returns {Promise} Promise object representing the API response
 */
export const getUserPosts = (userId, options = {}) => {
  const { page = 1, limit = 10 } = options;
  
  return api.get(`/users/${userId}/posts`, {
    params: {
      page,
      limit,
    },
  });
};

/**
 * Get community posts with accurate comment counts
 * @param {string} communityId - Community ID
 * @param {Object} options - Pagination options
 * @returns {Promise} Promise object representing the API response with accurate comment counts
 */
export const getCommunityPosts = (communityId, options = {}) => {
  const { page = 1, limit = 10 } = options;
  
  return api.get(`/communities/${communityId}/posts`, {
    params: {
      page,
      limit,
      includeAccurateCommentCounts: true
    }
  });
};

/**
 * Create a comment on a post
 * @param {Object} commentData - Comment data
 * @param {string} commentData.content - Comment content
 * @param {string} commentData.postId - ID of the post to comment on
 * @param {string} [commentData.parentId] - Optional parent comment ID for replies
 * @param {File} [commentData.media] - Optional media file
 * @returns {Promise} Promise object representing the API response
 */
export const createComment = (commentData) => {
  const { content, postId, parentId, media } = commentData;
  
  if (!content || !postId) {
    throw new Error('Comment content and post ID are required');
  }
  
  const formData = new FormData();
  formData.append('content', content);
  formData.append('postId', postId);
  
  if (parentId) {
    formData.append('parentId', parentId);
  }
  
  if (media) {
    formData.append('media', media);
  }
  
  return uploadApi('/comments', formData);
};

/**
 * Get comments for a post
 * @param {string} postId - Post ID
 * @param {Object} options - Query options
 * @returns {Promise} Promise object representing the API response
 */
export const getPostComments = (postId, options = {}) => {
  const { page = 1, limit = 10, parentOnly = true } = options;
  
  return api.get(`/comments/post/${postId}`, {
    params: {
      page,
      limit,
      parentOnly
    }
  });
};

/**
 * Like a comment
 * @param {string} commentId - Comment ID
 * @returns {Promise} Promise object representing the API response
 */
export const likeComment = (commentId) => {
  return api.put(`/comments/${commentId}/like`);
};

/**
 * Unlike a comment
 * @param {string} commentId - Comment ID
 * @returns {Promise} Promise object representing the API response
 */
export const unlikeComment = (commentId) => {
  return api.put(`/comments/${commentId}/unlike`);
};

/**
 * Delete a comment
 * @param {string} commentId - Comment ID
 * @returns {Promise} Promise object representing the API response
 */
export const deleteComment = (commentId) => {
  return api.delete(`/comments/${commentId}`);
};